/* =========================================================
   PB:NEW 2026-05-18 — Lazy video loader
   ---------------------------------------------------------
   <video> elements don't support loading="lazy" natively, so
   we defer their network cost manually:

     1. Author markup renders <video preload="none"> WITHOUT
        the autoplay attribute, tagged with
        data-pb-lazy-video + data-pb-lazy-autoplay="1".
        preload="none" guarantees no media bytes are fetched
        during initial page load — only the element is parsed.
     2. When the video nears the viewport, this script calls
        .load() and .play() (if autoplay was requested).
     3. When the video leaves the viewport we pause it (memory
        + battery). Re-entering plays from current position.

   Honors prefers-reduced-motion (videos stay paused).

   Exposes window.PBLazyVideo.refresh(root) so sections that
   render videos client-side can hook them in after the DOM
   mutates.
   ========================================================= */
(function () {
  if (window.PBLazyVideo) return; // singleton

  var prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function activate(video) {
    // PB:MODIFIED 2026-06-02 — split one-time load from per-entry play.
    // The observer pauses videos that leave the viewport; previously the
    // `pbLazyActivated` guard short-circuited the whole function on
    // re-entry, so .play() was never called again and the video stayed
    // paused (notably the vault cards on mobile when slid away and back).
    // Now the preload/load() work runs once, but play() runs every time
    // the element re-enters view.
    if (video.dataset.pbLazyActivated !== '1') {
      video.dataset.pbLazyActivated = '1';

      // Bump preload so the browser can fetch enough to start.
      if (video.getAttribute('preload') === 'none') {
        video.setAttribute('preload', 'auto');
        try { video.load(); } catch (e) {}
      }
    }

    if (!prefersReducedMotion && video.dataset.pbLazyAutoplay === '1') {
      // Restore the autoplay attribute (in case the user navigates away
      // and back — browsers re-honour autoplay on cached elements) and
      // explicitly call play(). Re-entering the viewport resumes playback.
      video.setAttribute('autoplay', '');
      if (video.paused) {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }
  }

  function pause(video) {
    if (video.dataset.pbLazyActivated !== '1') return;
    if (!video.paused) {
      try { video.pause(); } catch (e) {}
    }
  }

  function observe(video, observer) {
    if (video.dataset.pbLazyObserved === '1') return;
    video.dataset.pbLazyObserved = '1';
    observer.observe(video);
  }

  function makeObserver() {
    if (!('IntersectionObserver' in window)) {
      // No IO support — just activate everything immediately.
      return { observe: function (v) { activate(v); } };
    }
    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          activate(entry.target);
        } else if (entry.target.dataset.pbLazyActivated === '1') {
          pause(entry.target);
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });
  }

  var observer = makeObserver();

  function refresh(root) {
    var scope = root || document;
    var videos = scope.querySelectorAll('video[data-pb-lazy-video]');
    videos.forEach(function (v) { observe(v, observer); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(document); });
  } else {
    refresh(document);
  }

  window.PBLazyVideo = { refresh: refresh };
})();
